import { useTranslation } from "react-i18next";

import AddIcon from "@mui/icons-material/Add";
import { Button, Stack } from "@mui/material";
import { FormikProps } from "formik";

import { FormikTextField } from "../../../../../../formik/FormikTextField";
import { IOptionItem } from "../../../../../../formik/formik-autocomplete/FormikAutocomplete.types";
import { nameof } from "../../../../../../utils/nameof";
import { AddressDto, AssetPropertyRightKind, PendingDocumentMove } from "../../../../../api/ClientFileApi";
import { InventoryUploadArea } from "../../../../components/inventory-upload-area/InventoryUploadArea";
import { PropertyRightsFormValues, PropertyRightsMaxLength } from "../../OtherPropertyRightsView.types";
import {
  formatHomeAddress,
  kindConfigOf,
  rightIndexesOfKind,
  withAddedRight,
} from "../../OtherPropertyRightsView.utils";
import { useRemoveRight } from "../../hooks/useRemoveRight";
import { EntryQuestion } from "../entry-question/EntryQuestion";
import { TenancyRightBlock } from "./components/tenancy-right-block/TenancyRightBlock";

const TENANCY = "clients.client.debtSettlement.assetInventory.propertyRightsTab.tenancy";
const RIGHTS_FIELD = nameof<PropertyRightsFormValues>("propertyRights");
const CONFIG = kindConfigOf(AssetPropertyRightKind.Tenancy);

interface Props {
  clientId: string;
  formik: FormikProps<PropertyRightsFormValues>;
  homeAddress: AddressDto | undefined;
  claimOptions: IOptionItem[];
  onAddMove: (move: PendingDocumentMove) => void;
  onCancelMove: (documentId: string) => void;
}

export function TenancyRightsSection({ clientId, formik, homeAddress, claimOptions, onAddMove, onCancelMove }: Props) {
  const { t } = useTranslation();
  const removeRight = useRemoveRight(formik);
  const { hasTenancyRights } = formik.values.answers;
  const rights = formik.values.propertyRights;
  const blocks = rightIndexesOfKind(rights, CONFIG.kind).flatMap((index) => {
    const right = rights[index];
    return right ? [{ index, key: right.key }] : [];
  });

  return (
    <Stack gap={2}>
      <EntryQuestion formik={formik} config={CONFIG} info={t(`${TENANCY}.entryInfo`)} />
      {hasTenancyRights === false && (
        <FormikTextField
          id="property-rights-tenantOrOwnerOfHomeAddress"
          formik={formik}
          path="answers.tenantOrOwnerOfHomeAddress"
          label={t(`${TENANCY}.tenantOrOwner`, { address: homeAddress ? formatHomeAddress(homeAddress) : "" })}
          slotProps={{ input: { inputProps: { maxLength: PropertyRightsMaxLength.tenantOrOwner } } }}
        />
      )}
      {hasTenancyRights === true && (
        <>
          {blocks.map(({ index, key }, position) => (
            <TenancyRightBlock
              key={key}
              formik={formik}
              index={index}
              number={position + 1}
              claimOptions={claimOptions}
              onRemove={() => removeRight(index)}
            />
          ))}
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() =>
              void formik.setFieldValue(RIGHTS_FIELD, withAddedRight(formik.values.propertyRights, CONFIG.kind))
            }
            sx={{ alignSelf: "flex-end" }}
          >
            {t(`${TENANCY}.add`)}
          </Button>
          <InventoryUploadArea
            clientId={clientId}
            formik={formik}
            config={CONFIG}
            label={t(`${TENANCY}.upload`)}
            onAddMove={onAddMove}
            onCancelMove={onCancelMove}
          />
        </>
      )}
    </Stack>
  );
}
